// Copyright (c) 2020 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { Action } from "redux"
import { catchError, filter, mergeMap, withLatestFrom } from "rxjs/operators"
import { create } from "@bufbuild/protobuf"
import { t } from "@lingui/macro"
import * as Actions from "../actions"
import * as TimelineSlice from "../slices/timeline"
import * as TimelineAdditionalUiSlice from "../slices/additionalUi"
import { prepareListDaySummaryRequest } from "./daySummaries"
import type { RootState } from "../../../app/store"
import { showSnackBar } from "../../snackbar/actions"
import { Code, executorMutation, Init, isConnectError, timelineService } from "../../../app/rpc"
import {
  TrashStatus,
  TrashTimeRowsRequestSchema,
  TrashTimeRowsResponse,
} from "../../../generated/wisetime/user/timeline/v4/timeline_pb"

type TrashTimeRowsRpc = (request: Init<typeof TrashTimeRowsRequestSchema>) => Observable<TrashTimeRowsResponse>

export const trashTimeRows = (trashTimeRowsRpc: TrashTimeRowsRpc) =>
  (action$: Observable<Action>, state$: Observable<RootState>): Observable<Action> =>
    action$.pipe(
      filter(Actions.trashTimeRows.match),
      withLatestFrom(state$),
      mergeMap(([action, state]) => {
        const { timeRowIds = [], } = action.payload
        return concat(
          of(
            // mark rows as being trashed
            TimelineSlice.setUpdatingTimeRowsStatus({
              trashStatus: TrashStatus.TRASHED,
              timeRowIds,
            })
          ),
          trashTimeRowsRpc(action.payload)
            .pipe(
              mergeMap(_ => concat(
                // set trashed status for rows
                of(TimelineSlice.updateTimeRowsStatus({
                  trashStatus: TrashStatus.TRASHED,
                  timeRowIds,
                })),
                // remove trashed rows from action pane
                of(TimelineSlice.removeActiveTimeRowIds({ timeRowIds })),
                // remove pending trashing
                of(TimelineSlice.removeUpdatingTimeRowsStatus(timeRowIds)),
                of(TimelineAdditionalUiSlice.setBlinkTrashIcon(true)),
                of(Actions.listDaySummaries(prepareListDaySummaryRequest(state.timeline.activeDate.toString()))
                )
              )),
              catchError(e => concat(
                // unmark rows as being trashed
                of(TimelineSlice.removeUpdatingTimeRowsStatus(timeRowIds)),
                showErrorNotification(e)
              )),
            ),
        )
      }),
    )

const showErrorNotification = (e: unknown): Observable<Action> => {
  if (isConnectError(e)) {
    const { code } = e
    if (code === Code.NotFound) {
      return of(showSnackBar({
        message: t({ id: "msg_trash_time_rows_failed_not_found", message: "Failed to move time rows to trash. Could not find the target time row or timesheet." }),
        messageType: "ERROR",
      }))
    }
    if (code === Code.PermissionDenied) {
      return of(showSnackBar({
        message: t({ id: "msg_trash_time_rows_failed_permission_denied", message: "Failed to move time rows to trash. You do not have permission to trash the time row or timesheet." }),
        messageType: "ERROR",
      }))
    }
    if (code === Code.FailedPrecondition) {
      return concat(
        of(showSnackBar({
          message: t({ id: "msg_trash_time_rows_failed_precondition", message: "Failed to trash time rows. Time rows may be out of sync." }),
          messageType: "ERROR",
        })),
        of(Actions.onTimelineOutOfSync()),
      )
    }
  }
  return of(showSnackBar({
    message: t({ id: "msg_trash_time_rows_failed", message: "Failed to move time rows to trash. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
    messageType: "ERROR",
  }))
}

const trashTimeRowsRpc = (request: Init<typeof TrashTimeRowsRequestSchema>): Observable<TrashTimeRowsResponse> =>
  executorMutation(() => timelineService.trashTimeRows(create(TrashTimeRowsRequestSchema, request)))

export default trashTimeRows(trashTimeRowsRpc)
